import { NavLink } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  LayoutDashboard,
  Package,
  FolderOpen,
  FileText,
  Image,
  Award,
  Settings,
  MessageSquare,
  Mail,
  SlidersHorizontal,
  Filter,
  Search,
  MapPin,
  Database,
  Star,
  Building2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const navGroups = [
  {
    label: null,
    items: [
      { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    ],
  },
  {
    label: 'Content Management',
    items: [
      { to: '/admin/products', icon: Package, label: 'Products' },
      { to: '/admin/categories', icon: FolderOpen, label: 'Categories' },
      { to: '/admin/filters', icon: Filter, label: 'Filters' },
      { to: '/admin/configurator-templates', icon: SlidersHorizontal, label: 'Configurators' },
      { to: '/admin/gallery', icon: Image, label: 'Gallery' },
      { to: '/admin/certificates', icon: Award, label: 'Certificates' },
      { to: '/admin/seo', icon: Search, label: 'SEO' },
      { to: '/admin/settings', icon: Settings, label: 'Settings' },
    ],
  },
  {
    label: 'Sales & CRM',
    items: [
      { to: '/admin/leads', icon: MapPin, label: 'Sales Leads' },
      { to: '/admin/shortlist', icon: Star, label: 'Shortlist' },
      { to: '/admin/quotes', icon: FileText, label: 'Quote Requests' },
      { to: '/admin/subscribers', icon: Mail, label: 'Subscribers' },
      { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
      { to: '/admin/factory-info', icon: Building2, label: 'Factory Info' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/lead-stats', icon: Database, label: 'Lead Statistics' },
    ],
  },
]

interface AdminMobileNavProps {
  isOpen: boolean
  onClose: () => void
}

export function AdminMobileNav({ isOpen, onClose }: AdminMobileNavProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed left-0 top-0 bottom-0 w-72 bg-secondary-900 z-50 lg:hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-secondary-800">
              <div>
                <img
                  src="/images/logo-white.png"
                  alt="EngiMarineX"
                  className="h-16 w-auto object-contain"
                />
                <span className="text-xs text-secondary-400 mt-1 block">Admin</span>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-secondary-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
              {navGroups.map((group, groupIndex) => (
                <div key={groupIndex}>
                  {group.label && (
                    <p className="text-xs text-secondary-500 uppercase tracking-wider mb-2 px-4">
                      {group.label}
                    </p>
                  )}
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={'end' in item ? item.end : undefined}
                        onClick={onClose}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-primary-600 text-white'
                              : 'text-secondary-300 hover:bg-secondary-800 hover:text-white'
                          )
                        }
                      >
                        <item.icon className="w-5 h-5" />
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
